import React, { useEffect, useRef, useState } from "react";
import styles from "./AppDownloadInterstitial.module.scss";
import { remoteConfig } from "services/remoteConfig";
import { mergeTvAppPromptConfig, resolveTvAppPlatform, openAppStore, canShowPromptEntry, isPromptDismissed, markPromptDismissed } from "constants/TvAppPrompt";
import { pickImage } from "helpers/webpSupport";

import fireTvIcon from "images/icons/FireTV-Icon.png";
import fireTvIconWebp from "images/icons/FireTV-Icon.webp";
import rokuIcon from "images/icons/Roku-Icon-v2.png";
import rokuIconWebp from "images/icons/Roku-Icon-v2.webp";

const PLATFORM_ICONS = {
    firetv: { webp: fireTvIconWebp, fallback: fireTvIcon },
    roku: { webp: rokuIconWebp, fallback: rokuIcon },
};

const STORE_NAMES = { firetv: "Appstore", roku: "Channel Store", samsung: "Samsung Apps" };

// Separate key from the peek's: dismissing the big one-time ask still leaves
// the peek as the quiet persistent reminder.
const dismissKey = (platform) => `app_interstitial_dismissed_${platform}`;

/**
 * Full-screen "Download our app" ask for TV browsers, shown once per
 * config.reShowDays. TV remotes drive a clumsy virtual cursor, so unlike the
 * edge-pinned peek this is remote-first: the primary button is focused on
 * mount and Tab/arrow keys just toggle between the two buttons. Config comes
 * from the same TvAppPromptConfig as the peek (config.interstitialEnabled
 * turns this surface off on its own). Mounted once globally in Layout.
 */
const AppDownloadInterstitial = () => {
    const [prompt, setPrompt] = useState(null); // { platform, label, url, deepLinkUrl }
    const [closing, setClosing] = useState(false);
    // True from the "Get the app" click until the store takes over (or the
    // fallback fires). Fire OS can take several seconds to process the
    // amzn:// deep link — without visible feedback the TV looks crashed.
    const [launching, setLaunching] = useState(false);
    const primaryRef = useRef(null);
    const secondaryRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        remoteConfig.load().then(() => {
            if (cancelled) return;
            const platform = resolveTvAppPlatform();
            if (!platform) return;
            const config = mergeTvAppPromptConfig(remoteConfig.getTvAppPrompt());
            const entry = config.platforms[platform];
            if (!config.enabled || config.interstitialEnabled === false || !canShowPromptEntry(entry)) return;
            if (isPromptDismissed(dismissKey(platform), config.reShowDays)) return;
            setPrompt({ platform, ...entry });
        });
        return () => { cancelled = true; };
    }, []);

    // Focus the primary button so the remote can act without cursor travel.
    useEffect(() => {
        if (prompt) primaryRef.current?.focus();
    }, [prompt]);

    if (!prompt) return null;

    const close = () => {
        markPromptDismissed(dismissKey(prompt.platform));
        setClosing(true);
        // Unmount after the fade-out finishes.
        window.setTimeout(() => setPrompt(null), 350);
    };

    const getApp = () => {
        if (launching) return;
        // Keep the card up with a spinner while the OS processes the deep
        // link; dismiss-memory is written now so a store round-trip doesn't
        // re-show it. If nothing has taken over after the deep-link fallback
        // window (openAppStore falls back to the web store at 1.5s), fade out.
        markPromptDismissed(dismissKey(prompt.platform));
        setLaunching(true);
        openAppStore(prompt);
        window.setTimeout(() => {
            setClosing(true);
            window.setTimeout(() => setPrompt(null), 350);
        }, 6000);
    };

    const onKeyDown = (e) => {
        if (launching) {
            e.preventDefault();
            return;
        }
        if (e.key === "Escape") {
            close();
            return;
        }
        // Two-button focus trap covering both Tab and D-pad arrows.
        if (["Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
            e.preventDefault();
            const next = document.activeElement === primaryRef.current ? secondaryRef : primaryRef;
            next.current?.focus();
        }
    };

    const icon = PLATFORM_ICONS[prompt.platform];

    return (
        <div
            className={`${styles.overlay} ${closing ? styles.closing : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label={prompt.label}
            onKeyDown={onKeyDown}
        >
            <div className={styles.card}>
                {icon && <img src={pickImage(icon.webp, icon.fallback)} className={styles.icon} alt="" draggable={false} />}
                <h2 className={styles.title}>{prompt.label}</h2>
                <p className={styles.subtitle}>
                    {prompt.url
                        ? "Faster, smoother and built for your TV — free to download."
                        : <>Faster, smoother and built for your TV. {prompt.instructions}.</>}
                </p>
                <div className={styles.buttons}>
                    {prompt.url ? (
                        <button ref={primaryRef} className={styles.primary} onClick={getApp} disabled={launching} aria-busy={launching}>
                            {launching
                                ? <><span className={styles.spinner} aria-hidden="true" /> Opening the {STORE_NAMES[prompt.platform] || "store"}…</>
                                : "Get the app"}
                        </button>
                    ) : (
                        // No store URL (Samsung): nothing to open, so a single
                        // acknowledge button that just dismisses.
                        <button ref={primaryRef} className={styles.primary} onClick={close}>Got it</button>
                    )}
                    {/* Hidden while launching: it's inert then anyway, and the
                        longer "Opening…" label needs its room to avoid wrapping. */}
                    {prompt.url && !launching && (
                        <button ref={secondaryRef} className={styles.secondary} onClick={close}>
                            Maybe later
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppDownloadInterstitial;
